import React, { useContext } from "react";
import { Link } from "react-scroll";
import { motion } from "motion/react";
import Typical from "react-typical";

import { jobData, socialNetworkData } from "../constants";
import { ThemeContext } from "../themeProvider";
import heroBg from "../assets/webdev.svg";
import cloud from "../assets/cloudBg.png";
import cloudDark from "../assets/cloudDark.png";

const Home = () => {
  const theme = useContext(ThemeContext);
  const darkMode = theme.state.darkMode;

  return (
    <>
      <div
        className="relative min-h-screen"
        style={
          darkMode
            ? { backgroundImage: `url('${cloudDark}')`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }
            : { backgroundImage: `url('${cloud}'`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }
        }
      >
        {/* Gradient overlay for better text readability */}
        <div className={`absolute inset-0 ${
          darkMode 
            ? 'bg-gradient-to-b from-black/40 via-transparent to-black/60' 
            : 'bg-gradient-to-b from-white/40 via-transparent to-white/60'
        }`} />
        
        <main
          className="relative mx-auto max-w-7xl px-4 sm:px-6 md:mt-0 lg:px-8 flex flex-col md:flex-row items-center justify-center md:justify-between min-h-screen py-20 md:py-0"
          id="/"
        >
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center lg:text-left md:w-1/2 z-10"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`inline-block px-4 py-2 rounded-full mb-4 backdrop-blur-sm ${
                darkMode 
                  ? 'bg-gray-800/90 text-gray-300 border border-gray-700' 
                  : 'bg-blue-50/90 text-blue-600 border border-blue-200'
              }`}
            >
              <span className="text-sm font-medium">👋 Welcome to my portfolio</span>
            </motion.div>
            <h1 className="text-4xl tracking-tight font-extrabold sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={darkMode ? "block text-white" : "block text-gray-900"}
              >
                Hi, I'm{' '}
                <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                  Alexander
                </span>
              </motion.span>
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="block text-blue-500 z-0 mt-2"
              >
                <Typical
                  steps={[
                    "Technical Lead 🚀",
                    2000,
                    "Software Architect 🏗️",
                    2000,
                    "Full Stack Developer 💻",
                    2000,
                  ]}
                  loop={Infinity}
                />
              </motion.span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`
                mt-6 text-base sm:text-lg md:text-xl lg:text-2xl max-w-2xl leading-relaxed
                ${darkMode
                  ? " text-gray-200"
                  : " text-gray-700"
                }
                `}
            >
              Building scalable and innovative solutions. Currently {jobData.jobTitle} at{' '}
              <a 
                className="font-semibold text-blue-500 hover:text-blue-600 underline decoration-2 underline-offset-2" 
                href={jobData.companyWebSite} 
                target="_blank"
                rel="noopener noreferrer"
              >
                {jobData.companyName}
              </a>
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex md:justify-start gap-4 mt-8"
            >
              {socialNetworkData.map((el, index) => (
                <motion.a 
                  key={el.name} 
                  href={el.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-3 rounded-full transition-all duration-300 ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                >
                  <img alt={el.name} src={el.image} className="w-6 h-6" />
                </motion.a>
              ))}
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start w-full sm:w-auto"
            >
              <Link
                className="group relative px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden text-center"
                to="contact"
                smooth={true}
                offset={-80}
              >
                <span className="relative z-10">Get In Touch</span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              <Link
                className={`px-6 py-4 font-semibold rounded-xl border-2 transition-all duration-300 cursor-pointer hover:scale-105 text-center backdrop-blur-sm ${
                  darkMode 
                    ? 'border-gray-700 text-white hover:bg-gray-800/50 bg-gray-900/30' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100/50 bg-white/30'
                }`}
                to="about"
                smooth={true}
                offset={-80}
              >
                View My Work
              </Link>
            </motion.div>
          </motion.div>
          <motion.img
            initial="hidden"
            whileInView={"visible"}
            variants={{
              visible: {
                y: 0,
                opacity: 1,
                transition: {
                  type: "spring",
                },
              },
              hidden: { opacity: 1, y: 80 },
            }}
            src={heroBg}
            alt=""
            className="md:w-3/6 hidden sm:block"
          />
        </main>
      </div>
    </>
  );
};

export default Home;
